import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Organization } from "../../models/interfaces/organization";
import OrganizationUtil from "../organization-util";
import { createLogger } from "../logger";

const log = createLogger("UserCreationLimits");

/**
 * Shared user-creation limit logic used by the Clients (users) and Team
 * (team) modals. Sub-organizations enforce their own allocated slice of the
 * head organization's limits; head organizations enforce their remaining
 * capacity after sub-org allocations have been carved out.
 */
export const useUserCreationLimits = () => {
    const [overUserLimit, setOverUserLimit] = useState<number | null>(null);
    const [orgUsers, setOrgUsers] = useState<number>();
    const [orgUserLimit, setOrgUserLimit] = useState<number>();
    const [parentOrg, setParentOrg] = useState<Organization | null>();
    const { t } = useTranslation();

    /**
     * Loads the current usage and limit for the given organization.
     */
    const refreshLimits = useCallback(async (organization: Organization | null | undefined) => {
        if (!organization) {
            return;
        }

        try {
            const allUsers = await OrganizationUtil.getOrganizationUserCount(organization);
            setOrgUsers(allUsers);

            if (OrganizationUtil.isSubOrganization(organization)) {
                // sub-orgs enforce their own allocated slice of the head's limits
                setParentOrg(organization);
                setOrgUserLimit(organization.userLimit);
            } else if (organization.organizationLimit) {
                setParentOrg(organization);
                setOrgUserLimit(organization.userLimit);
            }
        } catch (e) {
            log.error("Failed to load organization user limits", {
                exception: e,
                organizationId: organization.id,
            });
        }
    }, []);

    /**
     * Validates a pending number of invites against the organization's
     * remaining capacity, storing the overage for the UI when exceeded.
     */
    const checkAccountHolderLimit = useCallback(async (numberOfEmails: number) => {
        if (parentOrg && parentOrg.userLimit != null) {
            const limit = Number(parentOrg.userLimit);
            const current = await OrganizationUtil.getOrganizationUserCount(parentOrg);
            const remaining = limit - current;
            if (numberOfEmails > remaining) {
                setOverUserLimit((limit - (current + numberOfEmails)) * -1);
            } else {
                setOverUserLimit(null);
            }
        }
    }, [parentOrg]);

    /**
     * Re-validates a pending batch at submit time so nothing is created when
     * the limit has been reached. Returns false when the batch exceeds the
     * remaining capacity.
     */
    const validateRemainingCapacity = useCallback(async (
        organization: Organization | null | undefined,
        count: number,
    ): Promise<boolean> => {
        const remaining = await OrganizationUtil.getRemainingUserCapacity(organization);
        if (count > remaining) {
            setOverUserLimit(count - remaining);
            log.warn("User creation blocked: organization at capacity", {
                organizationId: organization?.id,
                requestedUsers: count,
                remainingUsers: remaining,
            });
            return false;
        }
        return true;
    }, []);

    const getOverLimitMessage = useCallback((canShowDetails: boolean) => {
        if (orgUserLimit != null && orgUsers != null && canShowDetails) {
            return t('users.user_limit_exceeded', { orgUserLimit, overUserLimit });
        }
        return t('users.user_limit_reached');
    }, [orgUserLimit, orgUsers, overUserLimit, t]);

    return {
        overUserLimit,
        orgUsers,
        orgUserLimit,
        parentOrg,
        refreshLimits,
        checkAccountHolderLimit,
        validateRemainingCapacity,
        getOverLimitMessage,
    };
};